'use client';

import { c as _c } from "react/compiler-runtime";
import { chartIndexCrosshairPlugin } from '@/domains/pages/shared/chart-crosshair';
import { TIME_SERIES_INTERACTION } from '@/domains/pages/shared/chart-interaction';
import { positionChartExternalTooltip } from '@/domains/pages/shared/chart-external-tooltip';
import { buildSegmentOverTimeTooltipHtml } from '@/domains/pages/ai-proficiency/components/chart-tooltip';
import { formatSegmentTrendTooltipHeader } from '@/domains/pages/shared/trend-query';
import { AIFI_CHART_PRIMARY_COLOR, AIFI_CHART_SECONDARY_COLOR, AIFI_LINE_CHART_OPTIONS, AIFI_ORG_LINE_COLOR } from '@/domains/pages/ai-proficiency/constants';
import type { WorstBestEnvelopePoint } from '@/domains/pages/ai-proficiency/utils/segment-comparisons';
import { computePaddedYAxisBounds } from '@/domains/pages/shared/chart-axis';
import { orgLinePoints, ringHoverPoint, REPORT_LINE_SECONDARY_POINT_HOVER_RADIUS, smallSolidHoverPoint } from '@/domains/pages/ai-proficiency/utils/chart-hover-points';
import { themeColors } from '@/theme/palette';
import { AIFI_CHART_TICK_FONT } from '@/domains/pages/ai-proficiency/typography';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { Chart as ChartJS, ChartOptions, LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler } from 'chart.js';
import { useMemo, useRef, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartNoDataEmptyState } from '@/comps/charts/analytics-chart/ChartNoDataEmptyState';
ChartJS.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, chartIndexCrosshairPlugin);
type SegmentOverTimeChartProps = {
  labels: string[];
  selectedDisplayName: string;
  selectedScores: Array<number | null>;
  orgScores: Array<number | null>;
  otherSegmentScores: Record<string, Array<number | null>>;
  worstBestEnvelope: WorstBestEnvelopePoint[];
  segmentNoun: string;
  segmentNounPlural: string;
  computeTrendRank: (bucketIndex: number) => number;
  segmentCount: number;
};
const SEGMENT_COLOR = AIFI_CHART_PRIMARY_COLOR;
const ORG_COLOR = AIFI_ORG_LINE_COLOR;
const BAND_COLOR = `${AIFI_CHART_SECONDARY_COLOR}1A`;
const BAND_EDGE_COLOR = AIFI_CHART_SECONDARY_COLOR;
const HIDDEN_DATALABELS = {
  datalabels: {
    display: false
  }
};
const SEGMENT_OVER_TIME_TOOLTIP_ID = 'ai-proficiency-segment-over-time-tooltip';
function collectSegmentOverTimeYValues(orgScores: Array<number | null>, selectedScores: Array<number | null>, worstBestEnvelope: WorstBestEnvelopePoint[]): number[] {
  return [...orgScores.filter((score): score is number => score != null), ...selectedScores.filter((score): score is number => score != null), ...worstBestEnvelope.flatMap(point => [point.worst, point.best].filter((value): value is number => value != null))];
}
export function SegmentOverTimeChart(t0) {
  const $ = _c(59);
  const {
    labels,
    selectedDisplayName,
    selectedScores,
    orgScores,
    worstBestEnvelope,
    segmentNounPlural,
    computeTrendRank,
    segmentCount
  } = t0;
  const chartRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp2, t1);
  const hasData = labels.length > 0 && (selectedScores.some(_temp3) || orgScores.some(_temp4));
  let t2;
  if ($[1] !== orgScores || $[2] !== selectedScores || $[3] !== worstBestEnvelope) {
    t2 = computePaddedYAxisBounds(collectSegmentOverTimeYValues(orgScores, selectedScores, worstBestEnvelope));
    $[1] = orgScores;
    $[2] = selectedScores;
    $[3] = worstBestEnvelope;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const yAxisBounds = t2;
  let t3;
  if ($[5] !== worstBestEnvelope) {
    t3 = worstBestEnvelope.map(_temp5);
    $[5] = worstBestEnvelope;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const worstSeries = t3;
  let t4;
  if ($[7] !== worstBestEnvelope) {
    t4 = worstBestEnvelope.map(_temp6);
    $[7] = worstBestEnvelope;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const bestSeries = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = smallSolidHoverPoint(BAND_EDGE_COLOR);
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== worstSeries) {
    t6 = {
      label: "Worst",
      data: worstSeries,
      borderColor: "transparent",
      backgroundColor: BAND_COLOR,
      fill: "+1",
      tension: 0.35,
      spanGaps: true,
      order: 4,
      ...t5
    };
    $[10] = worstSeries;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = smallSolidHoverPoint(BAND_EDGE_COLOR);
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== bestSeries) {
    t8 = {
      label: "Best",
      data: bestSeries,
      borderColor: "transparent",
      backgroundColor: BAND_COLOR,
      fill: false,
      tension: 0.35,
      spanGaps: true,
      order: 3,
      ...t7
    };
    $[13] = bestSeries;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [4, 4];
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = ringHoverPoint(ORG_COLOR, REPORT_LINE_SECONDARY_POINT_HOVER_RADIUS);
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== orgScores) {
    t11 = {
      label: "Org",
      data: orgScores,
      borderColor: ORG_COLOR,
      borderDash: t9,
      borderWidth: 1.5,
      fill: false,
      tension: 0.35,
      spanGaps: false,
      order: 2,
      ...t10
    };
    $[17] = orgScores;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== selectedScores) {
    t12 = orgLinePoints(SEGMENT_COLOR, selectedScores);
    $[19] = selectedScores;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== selectedDisplayName || $[22] !== selectedScores || $[23] !== t12) {
    t13 = {
      label: selectedDisplayName,
      data: selectedScores,
      borderColor: SEGMENT_COLOR,
      borderWidth: 2,
      fill: false,
      tension: 0.35,
      spanGaps: false,
      order: 1,
      ...t12
    };
    $[21] = selectedDisplayName;
    $[22] = selectedScores;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t13 || $[27] !== t6 || $[28] !== t8) {
    t14 = [t6, t8, t11, t13];
    $[25] = t11;
    $[26] = t13;
    $[27] = t6;
    $[28] = t8;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== labels || $[31] !== t14) {
    t15 = {
      labels,
      datasets: t14
    };
    $[30] = labels;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const chartData = t15;
  let t16;
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      padding: {
        top: 4,
        right: 8,
        left: 0,
        bottom: 0
      }
    };
    t17 = {
      grid: {
        display: false
      },
      border: {
        display: false
      },
      ticks: {
        color: themeColors.text.secondary,
        font: AIFI_CHART_TICK_FONT,
        maxRotation: 0,
        autoSkip: true,
        maxTicksLimit: 12
      }
    };
    $[33] = t16;
    $[34] = t17;
  } else {
    t16 = $[33];
    t17 = $[34];
  }
  let t18;
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      display: true,
      color: "rgba(255, 255, 255, 0.12)"
    };
    t19 = {
      color: "rgba(255, 255, 255, 0.08)",
      tickLength: 4
    };
    $[35] = t18;
    $[36] = t19;
  } else {
    t18 = $[35];
    t19 = $[36];
  }
  let t20;
  if ($[37] !== yAxisBounds.stepSize) {
    t20 = {
      display: true,
      color: themeColors.text.secondary,
      font: AIFI_CHART_TICK_FONT,
      padding: 6,
      stepSize: yAxisBounds.stepSize,
      callback: _temp7
    };
    $[37] = yAxisBounds.stepSize;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t20 || $[40] !== yAxisBounds.max || $[41] !== yAxisBounds.min) {
    t21 = {
      x: t17,
      y: {
        min: yAxisBounds.min,
        max: yAxisBounds.max,
        border: t18,
        grid: t19,
        ticks: t20
      }
    };
    $[39] = t20;
    $[40] = yAxisBounds.max;
    $[41] = yAxisBounds.min;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      display: false
    };
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== computeTrendRank || $[45] !== labels || $[46] !== orgScores || $[47] !== segmentCount || $[48] !== segmentNounPlural || $[49] !== selectedDisplayName || $[50] !== selectedScores) {
    t23 = {
      ...HIDDEN_DATALABELS,
      legend: t22,
      tooltip: {
        enabled: false,
        external(context) {
          let tooltipEl = document.getElementById(SEGMENT_OVER_TIME_TOOLTIP_ID);
          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = SEGMENT_OVER_TIME_TOOLTIP_ID;
          }
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
          }
          const index = tooltipModel.dataPoints?.[0]?.dataIndex ?? 0;
          const selectedScore = selectedScores[index] ?? null;
          const orgScore = orgScores[index] ?? null;
          const rank = computeTrendRank(index);
          const header = formatSegmentTrendTooltipHeader(labels[index] ?? "", rank, segmentCount, segmentNounPlural);
          tooltipEl.innerHTML = buildSegmentOverTimeTooltipHtml({
            header,
            selectedName: selectedDisplayName,
            selectedScore,
            orgScore
          });
          positionChartExternalTooltip(context.chart, tooltipEl, tooltipModel);
        }
      }
    };
    $[44] = computeTrendRank;
    $[45] = labels;
    $[46] = orgScores;
    $[47] = segmentCount;
    $[48] = segmentNounPlural;
    $[49] = selectedDisplayName;
    $[50] = selectedScores;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t21 || $[53] !== t23) {
    t24 = {
      ...AIFI_LINE_CHART_OPTIONS,
      responsive: true,
      maintainAspectRatio: false,
      interaction: TIME_SERIES_INTERACTION,
      hover: TIME_SERIES_INTERACTION,
      layout: t16,
      scales: t21,
      plugins: t23
    };
    $[52] = t21;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  const options = t24;
  if (!hasData) {
    let t25;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <ChartNoDataEmptyState />;
      $[55] = t25;
    } else {
      t25 = $[55];
    }
    return t25;
  }
  let t25;
  if ($[56] !== chartData || $[57] !== options) {
    t25 = <div className="relative h-full min-h-55 w-full overflow-hidden" data-testid="segment-over-time-chart"><Line ref={chartRef} data={chartData} options={options} /></div>;
    $[56] = chartData;
    $[57] = options;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  return t25;
}
function _temp7(value) {
  return formatScore(Number(value), {
    decimals: 0
  });
}
function _temp6(point_0) {
  return point_0.best;
}
function _temp5(point) {
  return point.worst;
}
function _temp4(score) {
  return score != null;
}
function _temp3(score_0) {
  return score_0 != null;
}
function _temp2() {
  return _temp;
}
function _temp() {
  document.getElementById(SEGMENT_OVER_TIME_TOOLTIP_ID)?.remove();
}
type SegmentOverTimeLegendProps = {
  selectedDisplayName: string;
  segmentNoun: string;
  segmentNounPlural: string;
  className?: string;
};
export function SegmentOverTimeLegend(t0) {
  const $ = _c(13);
  const {
    selectedDisplayName,
    segmentNoun,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-wrap items-center gap-4 text-xs text-muted-foreground", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="h-0.5 w-4" style={{
      backgroundColor: SEGMENT_COLOR
    }} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== selectedDisplayName) {
    t3 = <span className="flex items-center gap-2">{t2}{selectedDisplayName}</span>;
    $[3] = selectedDisplayName;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="flex items-center gap-2"><span className="h-0.5 w-4 border-t border-dashed border-white/45" />Org</span>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="size-2 rounded-sm" style={{
      backgroundColor: BAND_EDGE_COLOR
    }} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== segmentNoun) {
    t6 = <span className="flex items-center gap-2">{t5}Worst to best {segmentNoun}</span>;
    $[7] = segmentNoun;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t1 || $[10] !== t3 || $[11] !== t6) {
    t7 = <div className={t1}>{t3}{t4}{t6}</div>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}